import * as angular from "angular";
import {PriceType} from "../types/price.type";
import {PriceMethodType} from "../types/price-method.type";

const dispatchModeTypes = {
  Default: 'Default',
  LeaveUnassigned: 'LeaveUnassigned',
  AutoAssign: 'AutoAssign',
  AutoForceAssign: 'AutoForceAssign',
  PutToAuction: 'PutToAuction',
  MoveToMarket: 'MoveToMarket'
};

const availableProgramPriceTypesByMethod = {
  [PriceMethodType.ServiceAreas]: [PriceType.ByDistance, PriceType.BySpot, PriceType.ByPackageSize],
  [PriceMethodType.Locations]: [PriceType.ByPackageSize, PriceType.ByTotalPackagesValues],
  [PriceMethodType.Vehicle]: [PriceType.ByDistance, PriceType.ByDistanceUnit],
};

const additionTypes = [
  'Package',
  'Order'
];

const toastMessages = {
  bundles: {
    createChild: {
      success: 'Child price plan bundle created successfully!',
      successAndRedirect: 'Child price plan bundle created successfully, Please notice you have been redirected.',
      error: 'Failed to create a child price plan bundle!'
    },
    edit: {
      success: 'Price plan bundle updated successfully!',
      error: 'Failed to update the price plan bundle!',
      stateValidationError: 'The current state is no longer valid due to the last price plan bundle update, redirected to the default view according to the first available price plan type'
    }
  },
  pricePlans: {
    updatePreferences: {
      success: 'Price plan preferences updated successfully!',
      error: 'Price plan preferences update failed!',
      noChanges: 'Preferences is up to date.'
    },
    updatePlan: {
      success: 'Zone(s) updated successfully!',
      error: 'Zone(s) update failed!'
    }
  }
};

const events = {
  serviceTime: {
    addEvent: 'add-event'
  },
  accountPricePlanUpdated: 'account-price-plan-updated',
  editBundle: 'edit-price-plan-bundle',
  updateGroupChanges: 'update-group-changes',
  pricePlanDetailsLoader: 'change-loader-state',
  pricePlanDetailsDisplayFooter: 'display-footer',
  zoneUpdateRequired: 'zone-update-required'
};

const serviceTime = {
  events: {
    states: ['InheritedDisabled', 'InheritedEnabled', 'OverridenDisabled', 'OverridenEnabled'],
    isEditable: ['OverridenDisabled', 'OverridenEnabled'],
    isParent: ['InheritedDisabled', 'InheritedEnabled'],
    //Used as a dropdown on event create dialog,
    dayOfWeek: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    isDragAndDropEnabled: {
      serviceTime: true,
      specialDay: true
    },
    isServiceTimeEnabled: true, //Whether to display the service time events.
    isSpecialDaysEnabled: true, //Whether to display the special day events.
    blockByState: ['InheritedDisabled'] //Whether to display the events with the corresponding state.
  },
  colorScheme: {
    classes: {
      serviceTime: ['calendar-service-time-event'], //A list of CSS classes to add for the event type.
      specialDay: ['calendar-special-day-event']
    },
    //Palette per state.
    serviceTime: {
      InheritedDisabled: 'brown',
      InheritedEnabled: 'cyan',
      OverridenDisabled: 'grey',
      OverridenEnabled: 'blue'
    },
    specialDay: {
      InheritedDisabled: 'green',
      InheritedEnabled: 'indigo',
      OverridenDisabled: 'purple',
      OverridenEnabled: 'amber'
    },
    hue: {
      inherit: 500,
      override: 900
    }
  },
  formats: {
    time: {
      US: 'h a',
      all: 'HH:mm'
    },
    date: {
      columnTitle: {
        US: 'ddd MM/DD',
        all: 'ddd DD/MM'
      },
      tooltip: {
        US: 'MM/DD',
        all: 'dd/MM'
      }
    }
  },
  tooltip: {
    header: {
      serviceTime: {
        inherit: 'Parent Recurring Weekly',
        override: 'Recurring Weekly'
      },
      specialDay: {
        inherit: 'Parent Special Occasion',
        override: 'Special Occasion'
      }
    }
  },
  //Used in the event dialog for the Hour/Minute dropdown selectors.
  steps: {
    hour: 1,
    minute: 1
  },
  createEventDefaults: {
    startTime: {
      hour: 8,
      minute: 0
    },
    endTime: {
      hour: 10,
      minute: 0
    },
    isSlotEnabled: true
  },

  //Full calendar configurations - https://fullcalendar.io/docs/.
  slotDuration: '01:00:00',
  slotLabelInterval: '02:00:00',
  defaultView: 'agendaWeek',
  buttonText: {
    today: 'Today',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    list: 'List'
  },
  header: {
    left: 'today, agendaWeek, month',
    center: 'prev title next',
    right: 'specialDay serviceTime'
  }
};

const defaults = {
  state: {
    billing: 'pricePlanView.details.service',
    account: 'accounts-detail-demand.price-plan.plan'
  }
};

const packageItemsDefaultSettings = [
  {name: "Pallet", icon: "PALLET"},
  {name: "Box", icon: "BOX"},
  {name: "Boxes", icon: "BOXES"},
  {name: "Small Box", icon: "SMALL_BOX"},
  {name: "Medium Box", icon: "MEDIUM_BOX"},
  {name: "Large Box", icon: "LARGE_BOX"},
  {name: "Long Box", icon: "LONG_BOX"},
  {name: "Flat Box", icon: "FLAT_BOX"},
  {name: "Containers", icon: "CONTAINERS"},
  {name: "Small Containers", icon: "SMALL_CONTAINER"},
  {name: "Big Containers", icon: "BIG_CONTAINER"},
  {name: "Cart", icon: "CART"},
  {name: "Bag", icon: "BAG"},
  {name: "Car", icon: "CAR"},
  {name: "Material", icon: "MATERIAL"},
  {name: "Suitcase", icon: "SUITCASE"},
  {name: "Safe", icon: "SAFE"},
  {name: "Freeze Item", icon: "FREEZE_ITEM"},
  {name: "Cooling Item", icon: "COOLING_ITEM"},
  {name: "Warm Item", icon: "WARM_ITEM"},
  {name: "Fragile Item", icon: "FRAGILE_ITEM"},
  {name: "Cannabis", icon: "CANNABIS"},
  {name: "Medical", icon: "MEDICAL"},
  {name: "Radioactive", icon: "RADIOACTIVE"},
  {name: "Electric", icon: "ELECTRIC"}
]


angular
  .module('bringoz.admin.pricePlan')
  .constant('pricePlanConstant', {
    dispatchModeTypes: dispatchModeTypes,
    availableProgramPriceTypesByMethod: availableProgramPriceTypesByMethod,
    additionTypes: additionTypes,
    serviceTime: serviceTime,
    events: events,
    toastMessages: toastMessages,
    defaults: defaults,
    packageItemsDefaultSettings: packageItemsDefaultSettings
  });
